import { specialistRequests } from "api/specialistApi";
import React, { useCallback, useEffect, useState } from "react";
import {
  BooleanField,
  Button,
  Datagrid,
  FunctionField,
  ListContextProvider,
  NumberField,
  ReferenceField,
  ResourceContextProvider,
  SimpleList,
  TextField,
  useNotify,
  useStore,
} from "react-admin";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import dataProvider from "server/dataProvider";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import CustomFilter from "features/CustomFilter";
import UserField from "server/components/Fields/UserField";
import { useNavigate } from "react-router";
import NotesIcon from "@mui/icons-material/Notes";
import { useNotifyError } from "utils/useNotifyError";

const NON_CLINICAL_CAMPAIGNS_ENROLLING_STORE_KEY = "nonClinicalCampaignsEnrolling.filter";

const ListNonClinicalCandidates = ({
  setShowForm,
  setSpecializationInfo,
  setNonClinicalUserInfo,
  setGraduationInfo,
}) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notify = useNotify();
  const navigate = useNavigate();
  const notifyError = useNotifyError();

  const [campaignId, setCampaignId] = useStore(NON_CLINICAL_CAMPAIGNS_ENROLLING_STORE_KEY, null);
  const [campaignsOptions, setCampaignsOptions] = useState([]);
  const [show, setShow] = useState(!!campaignId);

  const [nonClinicalCandidates, setNonClinicalCandidates] = useState([]);

  const fetchNonClinicalCandidates = useCallback(async () => {
    if (!campaignId) {
      notify("Моля въведете ID на кампания!", { type: "warning" });
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        specialistRequests.getSpecialistRankingsByCampaignId(campaignId, { isAccepted: true }),
        null,
        "GET",
      );
      setShow(true);
      setNonClinicalCandidates(data?.entries ?? []);
    } catch (err) {
      notifyError(err);
    }
  }, [campaignId, notify, notifyError]);

  const handleEnrollClick = useCallback(
    async rec => {
      if (!rec?.applicationId) return;

      try {
        const { data } = await dataProvider.customRequest(
          specialistRequests.getCampaignApplicationById(campaignId, rec?.applicationId),
          null,
          "GET",
        );
        setNonClinicalUserInfo({ ...data?.user, userId: data?.candidateUserId });
        setSpecializationInfo({
          specializationId: rec?.specializationId,
          receptionTypeId: rec?.receptionTypeId,
          trainingBuildingId: rec?.trainingBuildingId,
        });
        const { specialityId, degreeId, ...restGraduation } = data?.graduationInfo ?? {};
        setGraduationInfo({
          ...restGraduation,
          graduationSpecialityId: specialityId,
          graduationDegreeId: degreeId,
        });
        navigate({ search: `?campaignId=${campaignId}` });
        setShowForm(true);
      } catch (err) {
        notifyError(err);
      }
    },
    [setNonClinicalUserInfo, setSpecializationInfo, setGraduationInfo, setShowForm, navigate, campaignId, notifyError],
  );

  useEffect(() => {
    const getCampaigns = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          specialistRequests.getSpecialistCampaignsSelect(),
          null,
          "GET",
        );
        const options = data?.map(c => ({ id: c.id, name: c.nameBg })) ?? [];
        setCampaignsOptions(options);
      } catch (err) {
        notifyError(err);
      }
    };

    getCampaigns();
  }, [notifyError]);

  useEffect(() => {
    if (campaignId) fetchNonClinicalCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignsOptions.some(c => c.id === campaignId) ? campaignId : "",
            setValue: v => {
              setCampaignId(v);
              setShow(false);
            },
            type: "select",
            multiple: false,
            choices: campaignsOptions,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonOnClick={fetchNonClinicalCandidates}
      />
      {!show && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {show && campaignId && (
        <ResourceContextProvider value="non-clinical-candidates">
          <ListContextProvider value={{ data: nonClinicalCandidates }}>
            {isMobile ? (
              <SimpleList
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <MobileListRow
                    fields={[
                      {
                        label: "Кандидат",
                        value: (
                          <UserField
                            source="candidateUserId"
                            label="Кандидат"
                          />
                        ),
                      },
                    ]}
                  />
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow
                      fields={[
                        {
                          label: "Специалност",
                          value: (
                            <ReferenceField
                              source="specializationId"
                              reference={specialistRequests.getSpecializationsSelect()}
                              link={false}
                              sortable={false}
                            >
                              <FunctionField render={rec => `${rec?.nameBg} (${rec?.typeText})`} />
                            </ReferenceField>
                          ),
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Желание", value: record?.priority }]} />
                    <MobileListRow
                      fields={[
                        {
                          label: "Бал",
                          value: (
                            <NumberField
                              source="score"
                              options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                              textAlign="left"
                            />
                          ),
                        },
                      ]}
                    />
                    <MobileListRow fields={[{ label: "Приет", value: record?.isAccepted ? "Да" : "Не" }]} />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <Button
                        label="Записване"
                        variant="contained"
                        startIcon={<NotesIcon />}
                        onClick={() => handleEnrollClick(record)}
                      />
                    </div>
                  </div>
                )}
              />
            ) : (
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={{ ...settings.style?.secondaryDatagrid }}
              >
                <UserField
                  source="candidateUserId"
                  label="Кандидат"
                />
                <ReferenceField
                  source="specializationId"
                  reference={specialistRequests.getSpecializationsSelect()}
                  label="Специалност"
                  link={false}
                  sortable={false}
                >
                  <FunctionField render={rec => `${rec?.nameBg} (${rec?.typeText})`} />
                </ReferenceField>
                <TextField
                  source="priority"
                  label="Желание"
                  sortable={false}
                />
                <NumberField
                  source="score"
                  label="Бал"
                  sortable={false}
                  options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                  textAlign="left"
                />
                <BooleanField
                  source="isAccepted"
                  label="Приет"
                  sortable={false}
                />
                <FunctionField
                  render={rec => (
                    <Button
                      label="Записване"
                      variant="contained"
                      startIcon={<NotesIcon />}
                      onClick={() => handleEnrollClick(rec)}
                    />
                  )}
                />
              </Datagrid>
            )}
          </ListContextProvider>
        </ResourceContextProvider>
      )}
    </Box>
  );
};

export default ListNonClinicalCandidates;
